/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import {endOfDay} from 'date-fns'
import {makeAutoObservable, observable, runInAction, toJS} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {
  ArrUtil,
  HTMLUtil,
  logger,
  prepareVirtualize,
  Time,
  Timeout,
  TypeUtil,
  VirtualizeParams
} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerEntry,
  DeadlineStickerValue,
  getColumnColorNumber,
  Hub,
  noBoardContainerId,
  TaskDependencyData
} from 'yougile-model'
import {Items} from '@api/model/items'
import {addingAllowed} from '@client/hub-actions-allowed'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncUpdate} from '@client/sync-events'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {createTask} from '@components/add-task/create-task'
import {getDeadlineStatus} from '@components/aggregated/get-deadline-status'
import {DeadlineStatus} from '@components/aggregated/types'
import {BoardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {TableColumn, TableRow} from '@components/common/base/table'
import {MessageBox} from '@components/message-box'
import {asyncGetAllReportTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {LinkHelper} from '@components/timeline-new/link-helper'
import {timelineAutoPlanningPopupCtl} from '@components/timeline-new/timeline-auto-planning-popup'
import {createUseStore} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {BoxDragTarget, BoxHelper} from './box-helper'
import {DragHelper} from './drag-helper'
import {GraphHelper} from './graph-helper'
import {calculateHue} from './helpers/calculate-hue'
import {canChangeDeadline, canDrag} from './helpers/can-drag'
import {getExpandedDefault} from './helpers/get-expanded-default'
import {getShortedId, getShortedPath} from './helpers/get-shorted-path'
import {MeasureHelper} from './measure-helper'
import {timelineResizeStore} from './resize-store'
import {renderDateRange, renderLength, renderMenu, renderTitle} from './tasks'
import {renderAssigned} from './tasks/columns'
import {TitleHeader} from './tasks/columns/title-header'
import {
  ColumnId,
  CutoffData,
  DeadlineItem,
  ItemData,
  LinkData,
  Point,
  RowData,
  TimelineScale,
  VerticalStripeData
} from './types'
import {
  getNextScale,
  getPrevScale,
  getScaleData,
  hubToDeadlineItem,
  recurseSubtasks,
  startOfDay
} from './utils'

export const rowHeight = 40

export const virtualize: VirtualizeParams = {
  perPage: 20,
  positionToRow: top => Math.floor(top / rowHeight),
  rowToPosition: ix => ix * rowHeight,
  containerHeight: rows => rows * rowHeight
}

interface State {
  board?: Hub
  autoPlanning: boolean
  expandedPaths: Set<string>
  visibleColumns: Set<ColumnId>
  tableRows: TableRow<RowData>[]
  links: LinkData[]
  itemDataRegistry: Record<DataObjectId, ItemData>
  pathToData: Record<string, ItemData>
  cursorClass: string
  openedHubId: DataObjectId | undefined
  leftThreshold: number
  rightThreshold: number
  leftBoundary: number
  rightBoundary: number
  fromRow: number
  toRow: number
  // containers
  diagramEl: HTMLElement | undefined
  diagramContainerEl: HTMLElement | undefined
  graphEl: HTMLElement | undefined
  swimlanesEl: HTMLElement | undefined
  swimlanesContainerEl: HTMLElement | undefined
  tableScrollableEl: HTMLElement | undefined
  stripeContainerEls: HTMLElement[]
  // twTaskRef: HTMLElement | undefined
  // table editing
  editingHubId: DataObjectId | undefined
  editingPath: string | undefined
  editingColumn: ColumnId | undefined
  editingNewSubtask: boolean
  editingLinkFromPath: string | undefined
  editingLinkToPath: string | undefined
  editingClickPoint: Point
  // graph task adding
  taskAddingTimestamp: number | undefined
  // graph elements
  weekendStripes: VerticalStripeData[]
  intervalStripes: VerticalStripeData[]
  cutoffsTop: CutoffData[]
  cutoffsBottom: CutoffData[]
  // graph params
  scale: TimelineScale
  // box dragging
  draggingPath: string | undefined
  draggingHubId: DataObjectId | undefined
  draggingStart: number
  draggingEnd: number
  draggingTarget: BoxDragTarget | undefined
  // box linking
  linkingPath: string | undefined
  linkingHubId: DataObjectId | undefined
  linkingSwimlaneIx: number
  linkingPoint: Point

  showDiagramShadow: boolean
  // hover
  hoveredRowPath: string | undefined

  forceRootNodes: Hub[] | undefined
}

interface TimelineConfig {
  static: boolean
}

export class TimelineNewStore {
  static defaultConfig: TimelineConfig = {
    static: false
  }

  state: State = {
    board: undefined,
    autoPlanning: false,
    expandedPaths: new Set(),
    visibleColumns: new Set([
      ColumnId.title,
      ColumnId.dateRange,
      ColumnId.length
    ]),
    tableRows: [],
    links: [],
    itemDataRegistry: {},
    pathToData: {},
    cursorClass: '',
    openedHubId: undefined,
    leftThreshold: Number.POSITIVE_INFINITY,
    rightThreshold: Number.NEGATIVE_INFINITY,
    leftBoundary: Number.POSITIVE_INFINITY,
    rightBoundary: Number.NEGATIVE_INFINITY,
    fromRow: 0,
    toRow: 0,
    // table editing
    editingHubId: undefined,
    editingPath: undefined,
    editingColumn: undefined,
    editingNewSubtask: false,
    editingLinkFromPath: undefined,
    editingLinkToPath: undefined,
    editingClickPoint: {x: 0, y: 0},
    // graph task adding
    taskAddingTimestamp: undefined,
    // containers
    diagramEl: undefined,
    diagramContainerEl: undefined,
    graphEl: undefined,
    swimlanesEl: undefined,
    swimlanesContainerEl: undefined,
    tableScrollableEl: undefined,
    stripeContainerEls: [],
    // graph elements
    weekendStripes: [],
    intervalStripes: [],
    cutoffsTop: [],
    cutoffsBottom: [],
    // graph params
    scale: TimelineScale.Weeks,
    // box dragging
    draggingPath: undefined,
    draggingHubId: undefined,
    draggingStart: 0,
    draggingEnd: 0,
    draggingTarget: undefined,
    // box linking
    linkingPath: undefined,
    linkingHubId: undefined,
    linkingSwimlaneIx: 0,
    linkingPoint: {x: 0, y: 0},
    showDiagramShadow: false,
    // hover
    hoveredRowPath: undefined,
    forceRootNodes: undefined
  }

  readonly boxHelper: BoxHelper
  readonly graphHelper: GraphHelper
  readonly measureHelper: MeasureHelper
  readonly dragHelper: DragHelper
  readonly linkHelper: LinkHelper
  readonly boardSettingsStore: BoardSettingsStore

  recalcHandle: ReturnType<typeof requestAnimationFrame> | undefined
  clearLinkHandle: Timeout | undefined
  config: TimelineConfig

  constructor(
    boardSettingsStore: BoardSettingsStore,
    config: TimelineConfig = TimelineNewStore.defaultConfig
  ) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        boxHelper: observable.ref,
        graphHelper: observable.ref,
        measureHelper: observable.ref,
        dragHelper: observable.ref,
        linkHelper: observable.ref,
        boardSettingsStore: observable.ref,
        recalcHandle: observable.ref,
        clearLinkHandle: observable.ref
      },
      {autoBind: true}
    )

    this.boxHelper = new BoxHelper(this)
    this.graphHelper = new GraphHelper(this)
    this.measureHelper = new MeasureHelper(this)
    this.dragHelper = new DragHelper(this)
    this.linkHelper = new LinkHelper(this)
    this.boardSettingsStore = boardSettingsStore
    this.config = config
  }

  init(): () => void {
    this.syncLocation()

    const chatChangeListener = ChatUtils.addChatChangeListener(
      this.syncOpenedChat
    )

    const ganttUnsubscribe = Gantt.subscribe(this.calculateLinks)
    const ganttDraftUnsubscribe = GanttDraft.subscribe(this.calculateTableRows)

    return () => {
      this.linkHelper.destroy()

      if (this.recalcHandle) {
        cancelAnimationFrame(this.recalcHandle)
        this.recalcHandle = undefined
      }

      ChatUtils.removeChatChangeListener(chatChangeListener)

      ganttUnsubscribe()
      ganttDraftUnsubscribe()
    }
  }

  setForceRootNodes(nodes?: Hub[]): void {
    this.state.forceRootNodes = nodes
    this.syncLocation(false)
  }

  getRootNodes(): Hub[] {
    if (this.state.forceRootNodes) {
      return this.state.forceRootNodes
    }
    return [this.state.board].filter(TypeUtil.isTruthy)
  }

  updateRootNodes(): boolean {
    if (this.state.forceRootNodes) {
      return false
    }
    const prevBoard = this.state.board
    this.state.board = PageService.getCurrentBoard()
    return prevBoard !== this.state.board
  }

  get hasRootNodes(): boolean {
    if (this.state.forceRootNodes) {
      return true
    }
    return this.getRootNodes().length > 0
  }

  private getDefaultScale(): TimelineScale {
    const boardId = this.state.board?.id
    const data = localStorage.getItem('gantt-scale')

    if (data && boardId) {
      try {
        const map = JSON.parse(data) as {[boardId: DataObjectId]: TimelineScale}
        return map[boardId] ?? TimelineScale.Weeks
      } catch (e) {
        logger.warn('Cannot parse gantt-scale from localStorage', e)
      }
    }

    return TimelineScale.Weeks
  }

  private saveDefaultScale(value: TimelineScale): void {
    const boardId = this.state.board?.id
    const data = localStorage.getItem('gantt-scale')
    if (!boardId) {
      return
    }

    try {
      const map: {[boardId: DataObjectId]: TimelineScale} = data
        ? JSON.parse(data)
        : {}
      map[boardId] = value
      localStorage.setItem('gantt-scale', JSON.stringify(map))
    } catch (e) {
      logger.warn('Cannot parse gantt-scale from localStorage', e)
    }
  }

  private syncOpenedChat(): void {
    this.state.openedHubId = ChatUtils.getOpenedChatHub()?.id
  }

  private cleanup(): void {
    this.state.stripeContainerEls.length = 0
  }

  syncLocation(shouldResetScroll = true): void {
    this.cleanup()
    this.calcExpandedPaths()
    if (!this.hasRootNodes) {
      return
    }

    this.state.scale = this.getDefaultScale()
    if (shouldResetScroll) {
      this.resetTasksHorizontalScroll()
      this.graphHelper.scrollToToday()
    }
    this.recalcMeasure()
    this.calculateTableRows()
    this.syncAutoPlanning()
  }

  syncIfRequired(xs: SyncUpdate[]): void {
    if (xs.some(x => x.ids.some(id => this.syncFilter.has(id)))) {
      this.syncAutoPlanning()
      this.calculateTableRows()
    }
  }

  calcExpandedPaths(): boolean {
    const shouldSync = this.updateRootNodes()
    if (shouldSync) {
      this.loadExpandedPaths()
    }
    return shouldSync
  }

  syncAutoPlanning(): void {
    this.state.autoPlanning =
      this.state.board?.data?.ganttSettings?.autoPlanning ?? true

    if (this.state.autoPlanning && !Backend.features.ganttAutoPlanning) {
      this.toggleAutoPlanning()
    }
  }

  get syncFilter(): Set<DataObjectId> {
    const hubIds = this.state.tableRows.reduce((acc, x) => {
      const hub = x.data.hub

      if (hub) {
        acc.push(hub.id)

        // here it's correct code
        // here specified all possible mirrors without checking rights for subscription
        // usually to get mirrors use getAllMirroredHubs or getAllowedMirroredHubs
        const mirrors = hub.data?.mirrors
        if (mirrors) {
          acc.push(...mirrors)
        }
      }

      return acc
    }, [] as DataObjectId[])

    this.state.board && hubIds.push(this.state.board.id)
    return new Set(hubIds)
  }

  get tableColumns(): TableColumn<RowData>[] {
    // pl-8
    const columns: TableColumn<RowData>[] = [
      {
        id: ColumnId.title,
        title: <TitleHeader />,
        render: renderTitle,
        className: 'pl-24 pr-8 w-full',
        width: timelineResizeStore.getTitleWidth(),
        minWidth: timelineResizeStore.getTitleWidth()
      },
      // gap-8
      {
        id: ColumnId.assigned,
        title: Lang.msg.timeline.columnAssigned,
        render: renderAssigned,
        className: 'truncate flex justify-end',
        name: Lang.msg.timeline.columnAssignedFull,
        showTooltip: true,
        width: 52,
        minWidth: 52,
        maxWidth: 52
      },
      // gap-8
      {
        id: ColumnId.dateRange,
        title: Lang.msg.timeline.columnDateRange,
        hidden: !this.state.visibleColumns.has(ColumnId.dateRange),
        render: renderDateRange,
        className: 'pl-12 pr-8 truncate',
        cellClassName: cn(
          'rounded-4',
          !this.config.static && 'hover:bg-background-secondary'
        ),
        width: 150,
        minWidth: 150,
        maxWidth: 150
      },
      // gap-8
      {
        id: ColumnId.length,
        title: Lang.msg.timeline.columnLength,
        hidden: !this.state.visibleColumns.has(ColumnId.length),
        render: renderLength,
        className: 'pl-12 pr-8 truncate',
        cellClassName: cn(
          'rounded-4',
          !this.config.static && 'hover:bg-background-secondary'
        ),
        width: 72,
        minWidth: 72,
        maxWidth: 72
      }
    ]

    if (!this.config.static) {
      columns.push({
        id: ColumnId.menu,
        title: '',
        render: renderMenu,
        className: 'px-8 items-center justify-center truncate',
        width: 32,
        minWidth: 32,
        maxWidth: 32
      })
    }

    return columns
  }

  // expanded paths

  getExpanded(props: {type: string; path: string}): boolean {
    const {type} = props
    const path = getShortedPath(props.path)
    const defaultValue = getExpandedDefault({type})

    if (this.state.expandedPaths.has(path)) {
      return !defaultValue
    } else {
      return defaultValue
    }
  }

  // в localStorage храним не дефолтное поведение для схлапывания
  // при схлапывании колонки (это не дефолтное поведение) удаляем все что было схлопнуто внутри
  // при схлапывании задачи (это дефолтное поведение) удаляем все было схлопнуто внутри
  toggleExpanded(aPath: string, type: string, onlyOpen = false): void {
    const toggledPath = getShortedPath(aPath)
    const set = new Set(this.state.expandedPaths)

    const needDelete = set.has(toggledPath)

    if (needDelete) {
      if (onlyOpen) {
        return
      }

      // схлапывание задачи
      if (type !== 'column') {
        const arr: string[] = []
        for (const path of this.state.expandedPaths) {
          if (!path.startsWith(toggledPath)) {
            arr.push(path)
          }
        }
        this.state.expandedPaths = new Set(arr)
      } else {
        // расхлапывание колонки
        set.delete(toggledPath)
        this.state.expandedPaths = set
      }
    } else {
      // схлапывание колонки
      if (type === 'column') {
        const arr: string[] = []
        for (const path of this.state.expandedPaths) {
          if (!path.startsWith(toggledPath)) {
            arr.push(path)
          }
        }

        arr.push(toggledPath)
        this.state.expandedPaths = new Set(arr)
      } else {
        // расхлапывание задачи
        set.add(toggledPath)
        this.state.expandedPaths = set
      }
    }

    this.saveExpandedPaths()
    this.calculateTableRows()
  }

  loadExpandedPaths(): void {
    if (!this.state.board) {
      return
    }

    const obj = this.getSafeExpandedPaths()
    this.state.expandedPaths = new Set(obj[getShortedId(this.state.board.id)])
  }

  saveExpandedPaths(): void {
    if (!this.state.board) {
      return
    }

    const obj = this.getSafeExpandedPaths()
    obj[getShortedId(this.state.board.id)] = [...this.state.expandedPaths]
    localStorage.setItem('gantt-expanded-paths', JSON.stringify(obj))
  }

  getSafeExpandedPaths(): {[shortedBoardId: string]: string[]} {
    try {
      return JSON.parse(localStorage.getItem('gantt-expanded-paths') || '{}')
    } catch (e) {
      return {}
    }
  }

  //

  setDiagramEl(el: HTMLElement): void {
    this.state.diagramEl = el
  }

  setDiagramContainerEl(el: HTMLElement): void {
    this.state.diagramContainerEl = el
  }

  setGraphEl(el: HTMLElement): void {
    this.state.graphEl = el
  }

  setSwimlanesEl(el: HTMLElement): void {
    this.state.swimlanesEl = el
  }

  setSwimlanesContainer(el: HTMLElement): void {
    this.state.swimlanesContainerEl = el
  }

  setTableScrollabledEl(el: HTMLElement): void {
    this.state.tableScrollableEl = el
  }

  addStripeContainerEl(el: HTMLElement): void {
    this.state.stripeContainerEls.push(el)
  }

  scheduleRecalc(): void {
    if (!this.recalcHandle) {
      this.recalcHandle = requestAnimationFrame(() => {
        this.recalcHandle = undefined
        this.recalcMeasure()
      })
    }
  }

  private recalcMeasure(): void {
    this.measureHelper.calculateStripes()
    this.measureHelper.calculateCutoffs()
  }

  async calculateTableRows(): Promise<void> {
    if (!this.hasRootNodes) {
      return
    }

    const box = Sync.getCurrentSandbox()
    const rows: TableRow<RowData>[] = []
    const idToData: Record<DataObjectId, ItemData> = {}
    const pathToData: Record<string, ItemData> = {}
    const hueIndex: Record<DataObjectId, number> = {}
    let links: [DataObjectId, DataObjectId][] = []
    let leftThreshold = Number.POSITIVE_INFINITY
    let rightThreshold = Number.NEGATIVE_INFINITY

    type ItOpts = {
      hub: Hub
      level: number
      pathPrefix: string
      hue: number
      parentId: DataObjectId
      report?: boolean
      mirror?: boolean
      isSubtask?: boolean
    }

    const it = async (opts: ItOpts): Promise<void> => {
      const {hub, level, pathPrefix, parentId, isSubtask} = opts
      let {hue} = opts

      let children: Hub[] = []
      let deadline: DeadlineStatus = {status: 'in-progress', date: ''}
      let onClick:
        | undefined
        | ((event: React.MouseEvent<HTMLDivElement>) => void) = undefined

      const type = box.getType(hub)

      const reportCol = type === 'column' ? !!hub.data?.report : undefined
      const report = opts.report ?? reportCol
      const mirrorCol =
        type === 'column' ? Column.isMirror(hub) && !report : undefined
      const mirror = opts.mirror ?? mirrorCol

      if (type === 'task' && (report || mirror)) {
        hue = calculateHue(hueIndex, box, hub)
      } else if (type === 'column') {
        hue = getColumnColorNumber(hub).hue
      } else if (type === 'board') {
        hue = 1
      }

      if (type === 'task' && level === -1) {
        const column = box.getHub(hub.location)
        if (column) {
          hue = getColumnColorNumber(column).hue
        }
      }

      if (type === 'board') {
        children = Board.listColumns(hub, {sorted: true})

        const items: DeadlineItem[] = []
        for (const child of children) {
          let tasks: Hub[] = []

          if (child.data?.report) {
            tasks = (
              await asyncGetAllReportTasks(
                box,
                child.data.report,
                undefined,
                PageService.getCurrentBoard()
              )
            )
              .map(x => box.getHub(x.id))
              .filter(TypeUtil.isTruthy)
          } else if (Column.isMirror(child) && !child.data?.report) {
            tasks = Column.getMirroredColumns(child).flatMap(x =>
              Column.listTasks(x, false, {withoutArchive: true})
            )
          } else {
            tasks = Column.listTasks(child, false, {
              withoutArchive: true
            })
          }

          for (const task of tasks) {
            recurseSubtasks(task, x => {
              const current = GanttDraft.getDraft(x.id)?.deadline.current
              items.push(hubToDeadlineItem(x, current))
            })
          }
        }
        deadline = getDeadlineStatus({items})
      } else if (type === 'column') {
        if (hub.data?.report) {
          children = ArrUtil.defined(
            (
              await asyncGetAllReportTasks(
                box,
                hub.data.report,
                undefined,
                PageService.getCurrentBoard()
              )
            ).map(x => box.getHub(x.id))
          )
        } else if (Column.isMirror(hub) && !hub.data?.report) {
          children = Column.getMirroredColumns(hub)
        } else {
          if (!Column.isMirror(hub) && mirror) {
            hue = getColumnColorNumber(hub).hue
          }

          children = Column.listTasks(hub, false, {
            sorted: true,
            withoutArchive: true
          })
        }

        const items: DeadlineItem[] = []
        for (const task of children) {
          recurseSubtasks(task, x => {
            const current = GanttDraft.getDraft(x.id)?.deadline.current
            items.push(hubToDeadlineItem(x, current))
          })
        }
        deadline = getDeadlineStatus({items})
      } else if (type === 'task') {
        const list = SubtaskUtil.getSubtaskList(hub)
        children = SubtaskUtil.getSubtasksFromListAsHub(list).filter(
          hub => !Task.isArchived(hub)
        )
        const current = GanttDraft.getDraft(hub.id)?.deadline.current
        const items: DeadlineItem[] = [hubToDeadlineItem(hub, current)]
        deadline = getDeadlineStatus({items})

        onClick = this.onTaskClick(hub)
      }

      const expandable = level || type !== 'column' ? !!children.length : true
      const path = pathPrefix + '/' + hub.id
      const expanded = type === 'board' ? true : this.getExpanded({type, path})
      const completed = hub.data?.gtd?.state?.stage === 'done'
      const dragAllowed = globals.dragAllowed(hub.id)
      const draggable =
        type !== 'board' &&
        canDrag({hub, type, report, mirror, dragAllowed}) &&
        !this.config.static
      const changeDeadline =
        canChangeDeadline({type, dragAllowed}) && !this.config.static
      const linkable =
        report || mirror
          ? false
          : this.canEditLink(hub.id) && !this.config.static

      const data: RowData = {
        id: path,
        path,
        hubId: hub.id,
        hub,
        expandable,
        level,
        type,
        hue,
        parentId,
        completed,
        draggable: changeDeadline,
        linkable,
        report,
        mirror,
        isSubtask
      }

      // Transform into timestamps in a local timeframe
      deadline.start = deadline.start
        ? Time.toLocalDate(deadline.start).getTime()
        : undefined
      deadline.end = deadline.end
        ? Time.toLocalDate(deadline.end).getTime()
        : undefined

      if ((report || mirror) && idToData[hub.id]) {
        // skip
      } else {
        idToData[hub.id] = {deadline, row: data}
      }

      pathToData[path] = {deadline, row: data}

      if (deadline.end) {
        rightThreshold = Math.max(rightThreshold, deadline.end)
      }
      if (deadline.start) {
        leftThreshold = Math.min(leftThreshold, deadline.start)
      } else if (deadline.end) {
        leftThreshold = Math.min(leftThreshold, deadline.end)
      }

      const linkedFrom = report || mirror ? [] : Gantt.getLinkedFromHubs(hub)
      if (linkedFrom.length) {
        links = links.concat(linkedFrom.map(fromHub => [fromHub.id, hub.id]))
      }

      rows.push({
        data,
        className: 'h-40',
        onClick,
        draggable,
        hoverable: true
      })

      if (expanded) {
        for (const child of children) {
          await it({
            hub: child,
            level: level + 1,
            pathPrefix: path,
            hue,
            parentId: hub.id,
            report,
            mirror,
            isSubtask: type === 'task' && box.getType(child) === 'task'
          })
        }
      }

      const subtaskAdding =
        this.state.editingPath === path + '/new-task' &&
        this.state.editingNewSubtask

      const newTaskVisible =
        (expanded &&
          (type === 'column' || (type === 'task' && children.length))) ||
        (subtaskAdding && !this.config.static)

      const newTaskAllowed =
        !reportCol &&
        !mirrorCol &&
        (type === 'task'
          ? PermissionsChecker.checkEditSubtasks(hub)
          : addingAllowed(hub.id)) &&
        !this.config.static

      if (!reportCol && !mirrorCol && newTaskAllowed && newTaskVisible) {
        rows.push(this.prepareNewTaskRow(path, hub.id, hue, level + 1))
      }
    }

    const rootNodes = this.getRootNodes()
    for (const hub of rootNodes) {
      await it({
        hub: hub,
        level: -1,
        pathPrefix: '',
        hue: 1,
        parentId: hub.id
      })
    }

    if (addingAllowed(this.state.board?.id)) {
      rows.push(this.prepareNewColumnRow())
    }

    this.state.itemDataRegistry = idToData
    this.state.pathToData = pathToData
    this.state.tableRows = rows

    if (
      leftThreshold === Number.POSITIVE_INFINITY ||
      rightThreshold === Number.NEGATIVE_INFINITY
    ) {
      leftThreshold = Date.now()
      rightThreshold = Date.now()
    }
    leftThreshold = this.startTimeToSnappedTime(leftThreshold)
    rightThreshold = this.endTimeToSnappedTime(rightThreshold)

    this.state.leftThreshold = leftThreshold
    this.state.rightThreshold = rightThreshold

    this.calculateLinks()

    // Clear previously memoized data in case table rows are changed during drag
    this.dragHelper.prepareDragDataMemo.clear()

    this.virtualizeSwimlanes()
  }

  private calculateLinks(): void {
    const box = Sync.getCurrentSandbox()
    let links: [DataObjectId, DataObjectId][] = []

    for (const row of this.state.tableRows) {
      const hub = box.getHub(row.data.hubId)
      if (hub) {
        const linkedFrom = Gantt.getLinkedFromHubs(hub)
        if (linkedFrom.length) {
          links = links.concat(linkedFrom.map(fromHub => [fromHub.id, hub.id]))
        }
      }
    }

    if (!links.length) {
      this.state.links = []
      return
    }

    const pathToSwimlane: Record<string, number> = {}
    const idToPaths: Record<DataObjectId, string[]> = {}

    for (let i = 0; i < this.state.tableRows.length; ++i) {
      const data = this.state.tableRows[i].data
      if (data.report || data.mirror) {
        continue
      }
      pathToSwimlane[data.path] = i
      idToPaths[data.hubId] = [...(idToPaths[data.hubId] ?? []), data.path]
    }

    const res: LinkData[] = []
    for (const [fromHubId, toHubId] of links) {
      const fromPaths = idToPaths[fromHubId] ?? []
      const toPaths = idToPaths[toHubId] ?? []
      for (const fromPath of fromPaths) {
        for (const toPath of toPaths) {
          res.push({
            fromPath,
            toPath,
            fromSwimlane: pathToSwimlane[fromPath],
            toSwimlane: pathToSwimlane[toPath]
          })
        }
      }
    }

    res.sort((a, b) => {
      const dFromSwimlane = a.fromSwimlane - b.fromSwimlane
      if (dFromSwimlane !== 0) {
        return dFromSwimlane
      }
      const da = a.fromSwimlane - a.toSwimlane
      const db = a.fromSwimlane - b.toSwimlane
      if (da < 0 && db < 0) {
        return b.toSwimlane - a.toSwimlane
      } else if (da > 0 && db > 0) {
        return a.toSwimlane - b.toSwimlane
      } else {
        return 0
      }
    })

    this.state.links = res
  }

  private prepareNewTaskRow(
    parentPath: string,
    parentId: DataObjectId,
    hue: number,
    level: number
  ): TableRow<RowData> {
    const path = parentPath + '/new-task'
    return {
      data: {
        id: path,
        path,
        hubId: '' as DataObjectId,
        expandable: false,
        level,
        type: 'task',
        hue,
        parentId
      },
      className: 'h-40',
      draggable: false,
      hoverable: true
    }
  }

  private prepareNewColumnRow(): TableRow<RowData> {
    return {
      data: {
        id: '/new-column',
        path: '/new-column',
        hubId: '' as DataObjectId,
        expandable: false,
        level: 0,
        type: 'column',
        hue: 1,
        parentId: '' as DataObjectId
      },
      className: 'h-40',
      draggable: false,
      hoverable: true
    }
  }

  timeToPosition(ts: number): number {
    const [interval, pxPerInterval] = getScaleData(this.state.scale)
    return Math.floor(
      ((ts - this.state.leftBoundary) * pxPerInterval) / interval
    )
  }

  startTimeToSnappedTime(ts: number): number {
    switch (this.state.scale) {
      case TimelineScale.Days:
      case TimelineScale.Weeks:
      case TimelineScale.Months:
      case TimelineScale.Years:
        return startOfDay(new Date(ts)).getTime()
    }
  }

  endTimeToSnappedTime(ts: number): number {
    switch (this.state.scale) {
      case TimelineScale.Days:
      case TimelineScale.Weeks:
      case TimelineScale.Months:
      case TimelineScale.Years:
        return endOfDay(new Date(ts)).getTime()
    }
  }

  intervalToDistance(l: number): number {
    const [interval, pxPerInterval] = getScaleData(this.state.scale)
    const ds = (pxPerInterval * l) / interval
    return Math.ceil(ds)
  }

  distanceToInterval(w: number): number {
    const [interval, pxPerInterval] = getScaleData(this.state.scale)
    return Math.ceil((interval * w) / pxPerInterval)
  }

  distanceToSnappedDistance(w: number): number {
    const [interval, pxPerInterval] = getScaleData(this.state.scale)
    const dt = (Time.day * w) / pxPerInterval
    return (dt > 0 ? Math.floor : Math.ceil)(dt / interval) * interval
  }

  isCellEditing(path: string, column: ColumnId): boolean {
    return (
      this.state.editingPath === path && this.state.editingColumn === column
    )
  }

  startAddingSubtask(path: string): void {
    const ix = this.state.tableRows.findIndex(x => x.data.path === path)
    const parentRow = this.state.tableRows[ix]
    if (ix === -1 || !parentRow) {
      return
    }

    this.startEditing(undefined, path + '/new-task', ColumnId.title)
    this.state.editingNewSubtask = true
    this.toggleExpanded(path, 'new-task', true)

    this.calculateTableRows()
  }

  startEditing(
    hubId: DataObjectId | undefined,
    path: string,
    column: ColumnId
  ): void {
    this.state.editingHubId = hubId
    this.state.editingPath = path
    this.state.editingColumn = column
  }

  stopEditing(): void {
    this.state.editingHubId = undefined
    this.state.editingPath = undefined
    this.state.editingColumn = undefined
    this.state.editingNewSubtask = false
  }

  addBoardColumn(title?: string): void {
    title = title ? HTMLUtil.trimLineBreaks(title) : ''
    if (title && this.state.board) {
      const hub = Board.addColumn(title, this.state.board)
      void Tracking.send(TrackingEvent.ganttCreateColumnViaGanttChart)

      if (hub) {
        const path = '/' + hub.id
        this.toggleExpanded(path, 'column', true)
        this.setHoveredRow('/' + this.state.board?.id + '/' + hub.id)
      }
    }

    this.stopEditing()
  }

  addColumnTask(
    title: string | undefined,
    parentId: DataObjectId,
    path: string
  ): void {
    title = title ? HTMLUtil.trimLineBreaks(title) : ''
    if (!title) {
      this.stopEditing()
      return
    }

    const box = Sync.getCurrentSandbox()
    const parent = box.getHub(parentId)

    let newHubId: DataObjectId | undefined
    if (parent) {
      const parentType = box.getType(parent)
      const columnId = parentType === 'column' ? parent.id : noBoardContainerId
      const hubId = createTask({
        text: title,
        stickers: [], // todo required stickers!
        columnId,
        toEnd: true
      })

      if (parentType === 'column') {
        void Tracking.send(TrackingEvent.ganttCreateTaskViaGanttChart)
      } else if (parentType === 'task') {
        void Tracking.send(TrackingEvent.ganttCreateSubtaskViaGanttChart)
      }

      const hub = box.getHub(hubId)
      if (hub) {
        const tag = stickerCtl.Deadline.getSticker(box)
        // Same as in picker-section.tsx for deadline date picker w/o time
        const middleOfDayInUTC = new Date()
        middleOfDayInUTC.setUTCHours(12, 0, 0, 0)
        const current: DeadlineStickerEntry = {
          deadline: Time.toTimestamp(middleOfDayInUTC),
          startDate: Time.toTimestamp(middleOfDayInUTC),
          timestamp: Time.now(),
          withTime: false,
          by: globals.currentUserId
        }
        Sticker.pin(tag, hub.id, hub, {current, history: [current]})
        void Tracking.send(TrackingEvent.ganttSetDeadlineFirstTime)

        if (parentType !== 'column') {
          let list = SubtaskUtil.getSubtaskList(parent)
          list = SubtaskUtil.addSubtasks(list, [hub])
          SubtaskUtil.setSubtaskList(parent, toJS(list))
        }

        newHubId = hub.id
      }
    }

    const parentRow = this.state.itemDataRegistry[parentId]
    if (newHubId && parentRow) {
      const parentPathArr = path.split('/')
      parentPathArr.pop()
      const parentPath = parentPathArr.join('/')
      if (parentRow.row.type !== 'column') {
        this.toggleExpanded(parentPath, parentRow.row.type, true)
      }
      // hack: another row may be hovered after a new row is added and the list
      // scrolls, so wait two frames until everything renders before hovering
      // a new row
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setHoveredRow(parentPath + '/' + newHubId)
        })
      })
    }

    this.calculateTableRows()
  }

  editEntityTitle(title: string | undefined, hubId: DataObjectId): void {
    title = title ? HTMLUtil.trimLineBreaks(title) : ''
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(hubId)
    if (title && hub && hub.title !== title) {
      const canEdit = Items.rename(Items.fromHub(hub), title)
      if (canEdit) {
        box.editTitle(hub, title)
      }
    }

    this.stopEditing()
  }

  editTaskDeadline(
    value: DeadlineStickerValue | undefined,
    hubId: DataObjectId
  ): void {
    const box = Sync.getCurrentSandbox()
    const tag = stickerCtl.Deadline.getSticker(box)
    const hub = box.getHub(hubId)
    if (hub) {
      if (!Sticker.isPinned(tag, hub)) {
        void Tracking.send(TrackingEvent.ganttSetDeadlineFirstTime)
      } else {
        void Tracking.send(
          TrackingEvent.ganttChangeDeadlineViaGanttChartTaskList
        )
      }
      Sticker.pin(tag, hub.id, hub, value)
    }
  }

  clearTaskDeadline(hubId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const tag = stickerCtl.Deadline.getSticker(box)
    const hub = box.getHub(hubId)
    if (hub) {
      Sticker.unpin(tag, hub)
      Sticker.pin(tag, hub.id, hub)
    }
  }

  setCursorClass(cls: string): void {
    const prevClass = this.state.cursorClass
    this.state.cursorClass = cls
    prevClass && document.body.classList.remove(prevClass)
    cls && document.body.classList.add(cls)
  }

  onTableScrollY(): void {
    if (this.state.swimlanesEl && this.state.tableScrollableEl) {
      const tableScrollTop = this.state.tableScrollableEl.scrollTop
      this.state.swimlanesEl.scrollTop = tableScrollTop
      this.state.showDiagramShadow = tableScrollTop !== 0
      for (const el of this.state.stripeContainerEls) {
        if (el instanceof HTMLElement) {
          el.style.transform = `translate(0px,${tableScrollTop}px)`
        }
      }
      this.virtualizeSwimlanes()
    }
  }

  setScale(scale: TimelineScale): void {
    const scrollableEl = this.state.diagramEl
    if (!scrollableEl) {
      return
    }

    const dateFrom =
      this.state.leftBoundary +
      (scrollableEl.scrollLeft / scrollableEl.scrollWidth) *
        (this.state.rightBoundary - this.state.leftBoundary)

    const {width} = scrollableEl.getBoundingClientRect()
    const tCenter = dateFrom + this.distanceToInterval(width) / 2
    this.state.scale = scale
    this.saveDefaultScale(scale)
    const newDateFrom = tCenter - this.distanceToInterval(width) / 2
    this.graphHelper.resetBounds(newDateFrom)
    this.scheduleRecalc()

    void Tracking.send(TrackingEvent.ganttChangeScaleOfGanttChart, {scale})
  }

  onNextScale(): void {
    this.setScale(getNextScale(this.state.scale))
  }

  onPrevScale(): void {
    this.setScale(getPrevScale(this.state.scale))
  }

  private onTaskClick(task: Hub) {
    return (event: React.MouseEvent<HTMLDivElement>) => {
      // Do it only on the first click to allow double click
      if (event.detail > 1) {
        return
      }
      event.stopPropagation()

      const canShow = PermissionsChecker.checkShow(
        'ignore-departments',
        task,
        {}
      )
      if (task && canShow) {
        ChatUtils.toggleChat(task)
      } else {
        MessageBox.warn(Lang.msg.subtask.noPermissionForTask)
      }
    }
  }

  startLinkEdit(fromPath: string, toPath: string, e: React.MouseEvent): void {
    if (
      this.state.editingLinkFromPath === fromPath &&
      this.state.editingLinkToPath === toPath
    ) {
      this.cancelLinkEdit()
      return
    }

    const el = (e.target as HTMLElement).closest('div') as HTMLElement
    const rect = el.getBoundingClientRect()
    const clickPoint = {x: e.clientX - rect.x, y: e.clientY - rect.y}
    this.state.editingLinkFromPath = fromPath
    this.state.editingLinkToPath = toPath
    this.state.editingClickPoint = clickPoint

    // setTimeout prevents click from firing on the same bubbled click event
    clearTimeout(this.clearLinkHandle)
    setTimeout(() => {
      document.addEventListener('click', this.cancelLinkEditClickOut, {
        capture: true
      })
      document.addEventListener('keydown', this.cancelLinkEditEsc, {
        capture: true
      })
    })
  }

  private cancelLinkEditEsc(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation()
      e.stopImmediatePropagation()

      this.cancelLinkEdit()
    }
  }

  private cancelLinkEditClickOut(e: Event): void {
    if (
      e &&
      e.target instanceof HTMLElement &&
      e.target.closest('[data-tippy-root]')
    ) {
      return
    }

    this.cancelLinkEdit()
  }

  cancelLinkEdit(): void {
    document.removeEventListener('click', this.cancelLinkEditClickOut, {
      capture: true
    })
    document.removeEventListener('keydown', this.cancelLinkEditEsc, {
      capture: true
    })

    this.clearLinkHandle = setTimeout(() =>
      runInAction(() => {
        clearTimeout(this.clearLinkHandle)
        this.state.editingLinkFromPath = undefined
        this.state.editingLinkToPath = undefined
      })
    )
  }

  canEditLinkBetween(fromHubId: DataObjectId, toHubId: DataObjectId): boolean {
    const box = Sync.getCurrentSandbox()
    const fromHub = box.getHub(fromHubId)
    const toHub = box.getHub(toHubId)
    if (!fromHub || !toHub) {
      return false
    }

    return PermissionsChecker.checkConnect(fromHub, toHub)
  }

  canEditLink(hubId: DataObjectId): boolean {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(hubId)
    if (!hub) {
      return false
    }

    return PermissionsChecker.checkConnectSingle(hub)
  }

  editSelectedLinkData(data: TaskDependencyData): void {
    if (!this.state.editingLinkFromPath || !this.state.editingLinkToPath) {
      return
    }

    const fromData = this.state.pathToData[this.state.editingLinkFromPath]
    const toData = this.state.pathToData[this.state.editingLinkToPath]
    const fromHub = fromData.row.hub
    const toHub = toData.row.hub
    if (!fromHub || !toHub) {
      return
    }

    Gantt.linkTasks(fromHub, toHub, data)
  }

  removeLink(fromHubId: DataObjectId, toHubId: DataObjectId): void {
    if (!this.canEditLinkBetween(fromHubId, toHubId)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskConnect)
      return
    }

    Gantt.unlinkTasks(fromHubId, toHubId)
    this.cancelLinkEdit()
    this.calculateTableRows()

    void Tracking.send(TrackingEvent.ganttDeleteDependencyViaGanttChart)
  }

  resetTasksHorizontalScroll(): void {
    if (this.state.tableScrollableEl) {
      this.state.tableScrollableEl.scrollLeft = 0
    }
  }

  virtualizeSwimlanes(): void {
    const {swimlanesEl: scrollableEl, swimlanesContainerEl: containerEl} =
      this.state
    if (scrollableEl && containerEl) {
      const {scrollTop, clientHeight} = scrollableEl
      const {fromRow, toRow, height} = prepareVirtualize(
        scrollTop,
        clientHeight,
        this.state.tableRows.length,
        virtualize
      )

      runInAction(() => {
        this.state.fromRow = fromRow
        this.state.toRow = toRow
      })

      containerEl.style.height = `${height + 48}px`
    }
  }

  openGanttSettings(): void {
    this.boardSettingsStore.setCurrentTabId('gantt')
    this.boardSettingsStore.toggle()
  }

  toggleAutoPlanning(): void {
    const board = this.state.board
    if (!board) {
      return
    }
    const oldValue = board.data.ganttSettings?.autoPlanning ?? true
    const newValue = !oldValue

    const cb = (newValue: boolean) => {
      board.data.ganttSettings ??= {}
      board.data.ganttSettings.autoPlanning = newValue
      board.commit()

      if (newValue) {
        void Tracking.send(TrackingEvent.ganttEnableAutoPlanning)
      } else {
        void Tracking.send(TrackingEvent.ganttDisableAutoPlanning)
      }

      if (newValue) {
        GanttDraft.planEntitySubtree(board.id)
      }
    }

    if (newValue) {
      timelineAutoPlanningPopupCtl.onAccept = () => {
        delete timelineAutoPlanningPopupCtl.onAccept
        cb(newValue)
      }
      timelineAutoPlanningPopupCtl.open()
    } else {
      cb(newValue)
    }
  }

  get canEditAutoPlanning(): boolean {
    return this.state.board
      ? PermissionsChecker.checkBoardSettings(this.state.board)
      : false
  }

  emptySwimlaneTaskAddMouseMove(path: string, e: React.MouseEvent): void {
    if (this.state.hoveredRowPath !== path) {
      return
    }
    if (this.state.draggingPath || this.state.linkingPath) {
      return
    }

    const {clientX} = e
    const {diagramEl, leftBoundary} = this.state
    if (!diagramEl || !(e.target instanceof HTMLElement)) {
      return
    }

    const swimlane = e.target.closest('.group\\/swimlane')
    if (!swimlane) {
      return
    }

    const data = this.state.pathToData[path]
    if (!data || data.row.type !== 'task' || !data.row.hub) {
      return
    }

    const editable = PermissionsChecker.checkEditTaskStickers(
      data.row.hub,
      'not-user'
    )
    if (!editable) {
      return
    }

    e.stopPropagation()

    const delta = clientX - swimlane.getBoundingClientRect().left
    let taskAddingTimestamp = leftBoundary + this.distanceToInterval(delta)
    // Centers a cursor on button on different time scales
    if (this.state.scale === 'months' || this.state.scale === 'years') {
      const date = new Date(taskAddingTimestamp)
      const days = this.state.scale === 'years' ? 2 : 1
      date.setDate(date.getDate() - days)
      taskAddingTimestamp = date.getTime()
    }
    this.state.taskAddingTimestamp = taskAddingTimestamp
  }

  emptySwimlaneTaskAdd(path: string): void {
    const data = this.state.pathToData[path]
    if (!data || data.row.type !== 'task') {
      return
    }

    if (this.state.taskAddingTimestamp) {
      // if a task has no dependencies it can start&end on weekends
      // if a task has dependencies, it must be 1 or 3 working days long and start&end on weekdays
      const hasDeps = Gantt.getLinkedFromHubs(data.row.hubId).length > 0

      let startDate = new Date(Time.middleOfDay(this.state.taskAddingTimestamp))
      if (hasDeps) {
        startDate = Time.normalizeToWeekday(startDate, true)
      }

      let deadline = new Date(startDate)
      if (this.state.scale === 'months' || this.state.scale === 'years') {
        if (!hasDeps) {
          deadline = Time.normalizeToWeekday(deadline, true)
        }
        deadline = Time.addWorkingDays(deadline, 2)
      }

      const current: DeadlineStickerEntry = {
        timestamp: Time.now(),
        startDate: Time.toTimestamp(startDate),
        deadline: Time.toTimestamp(deadline),
        by: globals.currentUserId
      }
      this.editTaskDeadline({current, history: [current]}, data.row.hubId)

      if (this.state.autoPlanning) {
        GanttDraft.removeDraft(data.row.hubId)
        GanttDraft.planTaskByDependencies(data.row.hubId)
        GanttDraft.planDependentTasks(data.row.hubId)
      }
    }

    this.state.taskAddingTimestamp = undefined
  }

  public setHoveredRow(path: string | undefined): void {
    this.state.hoveredRowPath = path
    this.state.taskAddingTimestamp = undefined
  }
}

const [TimelineNewContext, useTimelineNewStore] =
  createUseStore(TimelineNewStore)
export {TimelineNewContext, useTimelineNewStore}

export const useTimelineConfig = (): TimelineConfig => {
  const [, store] = useTimelineNewStore()
  return store.config
}
